import { useId, useState } from 'react';

import { Collapse } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { RiArrowRightSLine } from '@remixicon/react';
import classNames from 'classnames';

import { Button, Text } from '@backstage/ui';

import {
  MAX_DEPTH,
  humanizeKey,
  isPlainObject,
  isRenderableScalar,
  isScalarArray,
  pluralize,
} from './inputUtils';

const useStyles = makeStyles({
  value: {
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  truncatedValue: {
    display: '-webkit-box',
    overflow: 'hidden',
    WebkitBoxOrient: 'vertical' as const,
    WebkitLineClamp: 5,
    wordBreak: 'break-word',
  },
  valuePlaceholder: {
    color: 'var(--bui-fg-secondary)',
  },
  fallback: {
    marginTop: 'var(--bui-space-1)',
    padding: 'var(--bui-space-2)',
    backgroundColor: 'var(--bui-bg-surface-2)',
    borderRadius: 'var(--bui-radius-1)',
    fontSize: 'var(--bui-font-size-2)',
    fontFamily: 'var(--bui-font-monospace)',
    color: 'var(--bui-fg-secondary)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    margin: 0,
  },
  booleanChip: {
    borderRadius: 'var(--bui-radius-1)',
    display: 'inline-block',
    fontSize: 'var(--bui-font-size-1)',
    fontWeight: 'var(--bui-font-weight-bold)' as unknown as number,
    lineHeight: 1,
    padding: 'var(--bui-space-1) var(--bui-space-2)',
  },
  booleanTrue: {
    backgroundColor:
      'color-mix(in srgb, var(--bui-fg-success) 10%, transparent)',
    color: 'var(--bui-fg-success)',
  },
  booleanFalse: {
    backgroundColor:
      'color-mix(in srgb, var(--bui-fg-danger) 10%, transparent)',
    color: 'var(--bui-fg-danger)',
  },
  chevron: {
    flexShrink: 0,
    transition: 'transform 150ms',
  },
  chevronExpanded: {
    transform: 'rotate(90deg)',
  },
  detailsToggle: {
    alignItems: 'center',
    alignSelf: 'flex-start',
    background: 'none',
    border: 'none',
    color: 'var(--bui-fg-secondary)',
    cursor: 'pointer',
    display: 'inline-flex',
    fontSize: 'var(--bui-font-size-2)',
    gap: 'var(--bui-space-1)',
    marginBlock: 'var(--bui-space-2)',
    padding: 0,
    '&:hover': {
      color: 'var(--bui-fg-primary)',
    },
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--bui-space-1)',
  },
  nestedGroup: {
    borderLeft: '2px solid var(--bui-border)',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 'var(--bui-space-2)',
    marginTop: 'var(--bui-space-1)',
    paddingLeft: 'var(--bui-space-3)',
  },
  extraRoot: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--bui-space-2)',
  },
});

const BooleanChip = ({ value }: { value: boolean }) => {
  const classes = useStyles();
  return (
    <span
      className={classNames(
        classes.booleanChip,
        value ? classes.booleanTrue : classes.booleanFalse,
      )}
    >
      {String(value)}
    </span>
  );
};

const StringValue = ({ value }: { value: string }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();
  const isLong = value.length > 200;

  if (!isLong) {
    return (
      <Text variant="body-small" className={classes.value}>
        {value}
      </Text>
    );
  }

  return (
    <>
      <Text
        variant="body-small"
        className={expanded ? classes.value : classes.truncatedValue}
        id={contentId}
      >
        {value}
      </Text>
      <Button
        variant="tertiary"
        size="small"
        aria-expanded={expanded}
        aria-controls={contentId}
        onPress={() => setExpanded(v => !v)}
      >
        {expanded ? 'Show less' : 'Show more'}
      </Button>
    </>
  );
};

export const ScalarValue = ({
  value,
}: {
  value: string | number | boolean | null | undefined;
}) => {
  const classes = useStyles();

  if (value === null || value === undefined) {
    return (
      <Text variant="body-small" className={classes.valuePlaceholder}>
        —
      </Text>
    );
  }

  if (typeof value === 'boolean') {
    return <BooleanChip value={value} />;
  }

  if (typeof value === 'string') {
    return <StringValue value={value} />;
  }

  return (
    <Text variant="body-small" className={classes.value}>
      {String(value)}
    </Text>
  );
};

export const ArrayValue = ({
  value,
}: {
  value: (string | number | boolean)[];
}) => {
  const classes = useStyles();
  return (
    <Text variant="body-small" className={classes.value}>
      {value.join(', ')}
    </Text>
  );
};

export const JsonFallback = ({ input }: { input: unknown }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();

  return (
    <>
      <Button
        variant="tertiary"
        size="small"
        aria-expanded={expanded}
        aria-controls={contentId}
        onPress={() => setExpanded(v => !v)}
      >
        {expanded ? 'Hide raw input' : 'Show raw input'}
      </Button>
      <Collapse in={expanded} unmountOnExit>
        <pre id={contentId} className={classes.fallback}>
          {JSON.stringify(input, null, 2)}
        </pre>
      </Collapse>
    </>
  );
};

export const FieldValue = ({
  value,
  depth = 0,
}: {
  value: unknown;
  depth?: number;
}) => {
  const classes = useStyles();

  if (isRenderableScalar(value)) {
    return <ScalarValue value={value} />;
  }
  if (isScalarArray(value)) {
    return <ArrayValue value={value} />;
  }
  if (depth >= MAX_DEPTH) {
    return (
      <Text variant="body-small" className={classes.value}>
        {JSON.stringify(value)}
      </Text>
    );
  }
  if (Array.isArray(value)) {
    const objectItems = value.filter(isPlainObject);
    if (objectItems.length === value.length && objectItems.length > 0) {
      const itemLabel = (o: Record<string, unknown>): string =>
        String(o.displayName ?? o.name ?? '?');
      const hasLabels = objectItems.some(
        o => typeof o.displayName === 'string' || typeof o.name === 'string',
      );
      const summary = hasLabels
        ? `${pluralize(objectItems.length, 'item')}: ${objectItems.map(itemLabel).join(', ')}`
        : pluralize(objectItems.length, 'item');
      return (
        <Text variant="body-small" className={classes.value}>
          {summary}
        </Text>
      );
    }
    return (
      <Text variant="body-small" className={classes.value}>
        {JSON.stringify(value)}
      </Text>
    );
  }
  if (isPlainObject(value)) {
    return (
      <div className={classes.nestedGroup}>
        {Object.entries(value).map(([key, v]) => (
          <div key={key} className={classes.row}>
            <Text variant="body-small" color="secondary">
              {humanizeKey(key)}
            </Text>
            <FieldValue value={v} depth={depth + 1} />
          </div>
        ))}
      </div>
    );
  }
  return (
    <Text variant="body-small" className={classes.value}>
      {JSON.stringify(value)}
    </Text>
  );
};

export const ExtraFields = ({ entries }: { entries: [string, unknown][] }) => {
  const classes = useStyles();
  const [expanded, setExpanded] = useState(false);
  const contentId = useId();

  return (
    <>
      <button
        type="button"
        className={classes.detailsToggle}
        aria-expanded={expanded}
        aria-controls={contentId}
        onClick={() => setExpanded(v => !v)}
      >
        {expanded ? 'Hide details' : 'Show details'}
        <RiArrowRightSLine
          size={12}
          className={classNames(classes.chevron, {
            [classes.chevronExpanded]: expanded,
          })}
        />
      </button>
      <Collapse in={expanded} unmountOnExit>
        <div id={contentId} className={classes.extraRoot}>
          {entries.map(([key, value]) => (
            <div key={key} className={classes.row}>
              <Text variant="body-small" color="secondary">
                {humanizeKey(key)}
              </Text>
              <FieldValue value={value} />
            </div>
          ))}
        </div>
      </Collapse>
    </>
  );
};
